import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import StatCard from "../components/StatCard";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { useYear } from "../context/YearContext";
import { DollarSign, TrendingDown, TrendingUp, Route, Wheat, Map, AlertTriangle, CalendarClock } from "lucide-react";
import ShopSavingsCard from "../components/ShopSavingsCard";
import RemindersCard from "../components/RemindersCard";
import PartnerOutstandingCard from "../components/PartnerOutstandingCard";
import CustomerOutstandingCard from "../components/CustomerOutstandingCard";
import OverrunEarningsCard from "../components/OverrunEarningsCard";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";

const CHART_COLORS = ["#1A4135", "#BC4727", "#E5A835", "#7A4B3A", "#8A9884", "#2A6F5A", "#D17A4B"];

export default function Dashboard() {
  const { year } = useYear();
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get(`/reports/summary?year=${year}`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [year]);

  useEffect(() => {
    api.get("/maintenance/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, [year]);

  const expenseData = summary
    ? Object.entries(summary.expenses_by_category).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }))
    : [];

  const fieldRevenue = summary
    ? Object.entries(summary.revenue_by_field).map(([name, value]) => ({ name, value }))
    : [];

  const profit = summary?.net_income ?? 0;
  const profitPositive = profit >= 0;

  return (
    <div>
      <TopBar
        title="Farm Dashboard"
        subtitle={`A one-page snapshot of revenue, costs, and tax-ready numbers for ${year}.`}
      />

      {alerts.length > 0 && (
        <div className="bg-[#FDF5E6] border border-[#E5A835] p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="maintenance-alerts">
          <div className="flex items-center gap-2 shrink-0">
            <AlertTriangle className="w-5 h-5 text-[#BC4727]" strokeWidth={2.2} />
            <div className="font-display font-bold text-sm uppercase tracking-wider">
              {alerts.filter((a) => a.status === "overdue").length > 0
                ? `${alerts.filter((a) => a.status === "overdue").length} overdue · `
                : ""}
              {alerts.length} maintenance alert{alerts.length > 1 ? "s" : ""}
            </div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2 text-xs">
            {alerts.slice(0, 4).map((a) => (
              <a
                key={a.id}
                href="/equipment"
                className={`px-2 py-1 border font-semibold ${
                  a.status === "overdue"
                    ? "bg-[#BC4727] text-white border-[#BC4727]"
                    : "bg-white border-[#E5A835]"
                }`}
                data-testid={`alert-${a.id}`}
              >
                <CalendarClock className="inline w-3 h-3 mr-1" />
                {a.equipment_name} · {a.title} ({a.status === "overdue" ? `${Math.abs(a.days_until_due)}d late` : `${a.days_until_due}d`})
              </a>
            ))}
            {alerts.length > 4 && <span className="text-[#66645E] px-2 py-1">+{alerts.length - 4} more</span>}
          </div>
        </div>
      )}

      <RemindersCard />
      <PartnerOutstandingCard />
      <CustomerOutstandingCard />
      <OverrunEarningsCard />
      <ShopSavingsCard year={year} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard
          testid="stat-revenue"
          label="Gross Revenue"
          value={summary ? fmtUSD(summary.total_revenue) : "—"}
          hint={`${summary?.yield_count || 0} harvest logs`}
          icon={TrendingUp}
          accent="bg-[#1A4135]"
        />
        <StatCard
          testid="stat-expenses"
          label="Input Costs"
          value={summary ? fmtUSD(summary.total_expenses) : "—"}
          hint={`${summary?.expense_count || 0} expense entries`}
          icon={TrendingDown}
          accent="bg-[#BC4727]"
        />
        <StatCard
          testid="stat-mileage"
          label="Mileage Deduction"
          value={summary ? fmtUSD(summary.mileage_deduction) : "—"}
          hint={summary ? `${fmtNum(summary.total_miles_business, 1)} business mi @ $${summary.mileage_rate}` : ""}
          icon={Route}
          accent="bg-[#7A4B3A]"
        />
        <StatCard
          testid="stat-net"
          label={profitPositive ? "Net Income" : "Net Loss"}
          value={summary ? fmtUSD(profit) : "—"}
          hint={summary?.depreciation > 0 ? `Incl. ${fmtUSD(summary.depreciation)} depreciation` : "Revenue − Expenses − Mileage"}
          icon={DollarSign}
          accent={profitPositive ? "bg-[#1A4135]" : "bg-[#BC4727]"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="bg-white border border-[#1F1E1B] p-6 lg:col-span-2" data-testid="chart-expenses">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">
                Cost Breakdown
              </div>
              <h3 className="font-display text-xl font-bold text-[#1F1E1B]">Input Costs by Category</h3>
            </div>
          </div>
          {expenseData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={expenseData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E5E2D6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#66645E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#66645E" }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  formatter={(v) => fmtUSD(v)}
                  contentStyle={{ border: "1px solid #1F1E1B", borderRadius: 0, background: "#fff" }}
                />
                <Bar dataKey="value" fill="#BC4727">
                  {expenseData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-16 text-center text-sm text-[#66645E]">No expenses logged for {year}.</div>
          )}
        </div>

        <div className="bg-white border border-[#1F1E1B] p-6" data-testid="chart-fields">
          <div className="mb-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">
              Harvest Revenue
            </div>
            <h3 className="font-display text-xl font-bold text-[#1F1E1B]">By Field</h3>
          </div>
          {fieldRevenue.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={fieldRevenue} dataKey="value" nameKey="name" outerRadius={85} stroke="#1F1E1B">
                  {fieldRevenue.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ border: "1px solid #1F1E1B", borderRadius: 0 }} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-16 text-center text-sm text-[#66645E]">No yield records.</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <StatCard testid="stat-fields-count" label="Fields Tracked" value={summary?.field_count ?? 0} icon={Map} accent="bg-[#1A4135]" />
        <StatCard testid="stat-harvest-count" label="Harvest Entries" value={summary?.yield_count ?? 0} icon={Wheat} accent="bg-[#E5A835]" />
        <StatCard testid="stat-expense-count" label="Expense Entries" value={summary?.expense_count ?? 0} icon={TrendingDown} accent="bg-[#BC4727]" />
        <StatCard testid="stat-mile-count" label="Mileage Logs" value={summary?.mileage_count ?? 0} icon={Route} accent="bg-[#7A4B3A]" />
      </div>
    </div>
  );
}
